import type { Question, GameType, Difficulty } from '../types';
import { generateUUID } from './uuid';

const DIFFICULTY_RANGES: Record<Difficulty, { min: number; max: number }> = {
  easy:   { min: 1, max: 5 },
  medium: { min: 1, max: 10 },
  hard:   { min: 1, max: 12 },
};

const DIFFICULTY_TIME_MULTIPLIERS: Record<Difficulty, number> = {
  easy:   1.5,
  medium: 1.0,
  hard:   0.75,
};

export function getOperatorSymbol(gameType: GameType): '+' | '-' | '×' | '÷' {
  switch (gameType) {
    case 'addition':       return '+';
    case 'subtraction':    return '-';
    case 'division':       return '÷';
    case 'multiplication': return '×';
  }
}

function generateWrongAnswers(correct: number, count: number, maxValue: number): number[] {
  const options = [correct];
  const used = new Set([correct]);

  while (options.length < count + 1) {
    let wrong: number;
    if (Math.random() < 0.7) {
      const offset = Math.floor(Math.random() * 6) - 3;
      wrong = correct + offset;
    } else {
      wrong = Math.floor(Math.random() * Math.max(maxValue, correct + 5)) + 1;
    }
    if (wrong > 0 && wrong !== correct && !used.has(wrong)) {
      options.push(wrong);
      used.add(wrong);
    }
  }
  return options;
}

export function generateQuestion(level: number, difficulty: Difficulty = 'medium', gameType: GameType = 'multiplication'): Question {
  const range = DIFFICULTY_RANGES[difficulty];
  const num2 = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;

  let num1: number;
  let correctAnswer: number;
  const operator = getOperatorSymbol(gameType);

  switch (gameType) {
    case 'multiplication': {
      num1 = level;
      correctAnswer = num1 * num2;
      break;
    }
    case 'division': {
      // dividend ÷ level = num2, so dividend = level * num2
      num1 = level * num2;
      correctAnswer = num2;
      break;
    }
    case 'addition': {
      num1 = level;
      correctAnswer = num1 + num2;
      break;
    }
    case 'subtraction': {
      // (level + num2) − level = num2
      num1 = level + num2;
      correctAnswer = num2;
      break;
    }
  }

  const maxAnswer = gameType === 'multiplication' ? level * range.max :
                    gameType === 'division'       ? range.max :
                    gameType === 'addition'       ? level + range.max :
                                                    range.max;

  const options = generateWrongAnswers(correctAnswer, 3, maxAnswer);

  return {
    id: generateUUID(),
    num1,
    num2: (gameType === 'division' || gameType === 'subtraction') ? level : num2,
    operator,
    correctAnswer,
    options: shuffle(options)
  };
}

export function shuffle<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function generateRandomMixQuestion(difficulty: Difficulty = 'medium', gameType: GameType = 'multiplication'): Question {
  const level = Math.floor(Math.random() * 10) + 1;
  return generateQuestion(level, difficulty, gameType);
}

export function getLevelTimeGoal(level: number, difficulty: Difficulty = 'medium'): number {
  const baseTimes = [180, 150, 150, 120, 120, 120, 105, 105, 105, 90];
  const base = baseTimes[level - 1] || 90;
  return Math.round(base * DIFFICULTY_TIME_MULTIPLIERS[difficulty]);
}

export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}